import React, { useEffect, useState } from 'react';
import './EfectoPremio.css';
import { Icono } from './iconos';
// Las fotos van por el helper compartido: traduce las URLs de SofaScore y Cloudinary al CDN
// propio (images.footy-games.com.ar). Con player.img crudo la mayoria de los jugadores quedaba
// sin foto en las cartas de la ruleta y de la apuesta.
import { getImgSrc } from '../../helpers/getImageSrc';
import defaultPlayerImg from '../../img/defaultPlayer.jpg';

const fmtM = (v) => v < 1e6 ? `€${Math.round(v / 1e3)}k` : `€${(v / 1e6).toFixed(v < 1e7 ? 1 : 0)}M`;

/** Carta de jugador, la pieza que usan casi todos los efectos. */
function Carta({ jugador, clase = '' }) {
    if (!jugador) return null;
    return (
        <div className={`ep-carta ${clase}`}>
            <div className="ep-carta-brillo" />
            <img src={getImgSrc(jugador.img) || defaultPlayerImg} alt=""
                onError={e => { e.target.src = defaultPlayerImg; }} />
            <span className="ep-carta-nombre">{jugador.nombre}</span>
            <span className="ep-carta-valor">{fmtM(jugador.valor)}</span>
        </div>
    );
}

/**
 * La apuesta: cinco cartas dadas vuelta. Das vuelta una y decidís si seguir.
 *
 * La regla es una escalera: cada carta nueva tiene que valer igual o más que la anterior. Si sale
 * una más barata te "pasás" y te quedás con la más barata de las cinco. Frenar en cualquier
 * momento te deja con la última que diste vuelta.
 */
function Apuesta({ cartas, onResolver }) {
    const [vueltas, setVueltas] = useState(0);
    const [estado, setEstado] = useState('jugando');   // jugando | plantado | pasado
    const [final, setFinal] = useState(null);

    const actual = vueltas > 0 ? cartas[vueltas - 1] : null;
    const masBarata = cartas.reduce((a, b) => (b.valor < a.valor ? b : a), cartas[0]);

    const darVuelta = () => {
        const siguiente = cartas[vueltas];
        const anterior = actual;
        setVueltas(v => v + 1);
        if (anterior && siguiente.valor < anterior.valor) {
            setEstado('pasado');
            setFinal(masBarata);
        } else if (vueltas + 1 === cartas.length) {
            setEstado('plantado');
            setFinal(siguiente);
        }
    };

    const plantarse = () => { setEstado('plantado'); setFinal(actual); };

    return (
        <div className="ep-apuesta">
            <div className="ep-mazo">
                {cartas.map((c, i) => {
                    const dada = i < vueltas;
                    const esFinal = final && c === final;
                    return (
                        <div key={i} className={`ep-naipe${dada ? ' dado' : ''}${esFinal ? ' elegido' : ''}`}>
                            {dada
                                ? <Carta jugador={c} />
                                : <div className="ep-dorso"><span>?</span></div>}
                        </div>
                    );
                })}
            </div>

            {estado === 'jugando' && (
                <>
                    <p className="ep-apuesta-texto">
                        {actual
                            ? <>Tenés a <strong>{actual.nombre}</strong> ({fmtM(actual.valor)}). La próxima tiene que valer igual o más.</>
                            : <>Cada carta nueva tiene que valer igual o más que la anterior.</>}
                    </p>
                    <div className="deal-botones">
                        <button className="deal-si" onClick={darVuelta}>
                            {vueltas === 0 ? 'Dar vuelta la primera' : 'Seguir'}
                        </button>
                        {actual && <button className="deal-no" onClick={plantarse}>Frenar</button>}
                    </div>
                </>
            )}

            {estado === 'pasado' && (
                <>
                    <p className="ep-apuesta-texto ep-perdiste">
                        Te pasaste: <strong>{cartas[vueltas - 1].nombre}</strong> ({fmtM(cartas[vueltas - 1].valor)}) vale menos.
                        Te quedás con el más barato de las cinco.
                    </p>
                    <button className="deal-si" onClick={() => onResolver(final)}>
                        Quedarme con {final.nombre} ({fmtM(final.valor)})
                    </button>
                </>
            )}

            {estado === 'plantado' && (
                <>
                    <p className="ep-apuesta-texto ep-ganaste">
                        Te quedás con <strong>{final.nombre}</strong> ({fmtM(final.valor)}).
                    </p>
                    <button className="deal-si" onClick={() => onResolver(final)}>Sumarlo al equipo</button>
                </>
            )}
        </div>
    );
}

/**
 * La puesta en escena de cada premio. Cada uno tiene su propia animación en vez de un cartel
 * genérico: el sobre se abre, la calavera parte la carta, los cambios giran una carta sobre la
 * otra. La secuencia avanza sola por `paso`.
 */
export default function EfectoPremio({ premio, detalle, onListo, onApuesta }) {
    const [paso, setPaso] = useState(0);

    useEffect(() => {
        // Cada efecto tiene su propio ritmo: el sobre necesita el suspenso del pack opening.
        const tiempos = { sobre: [900, 1500], calavera: [700, 1400], switch_bueno: [800, 1300], switch_malo: [800, 1300] };
        const t = tiempos[premio.id] || [700];
        const timers = t.map((ms, i) => setTimeout(() => setPaso(i + 1), t.slice(0, i + 1).reduce((a, b) => a + b, 0)));
        return () => timers.forEach(clearTimeout);
    }, [premio.id]);

    return (
        <div className={`ep-escena ep-${premio.id} ep-paso-${paso}`}>
            {premio.id === 'sobre' && (
                <div className="ep-sobre-wrap">
                    <div className="ep-rayos" />
                    <svg viewBox="0 0 120 120" className="ep-sobre">
                        <Icono nombre="sobre" cx={60} cy={60} size={110} />
                    </svg>
                    {paso >= 2 && detalle?.jugador && <Carta jugador={detalle.jugador} clase="ep-vuela" />}
                    {paso >= 2 && <div className="ep-chispas">{Array.from({ length: 14 }).map((_, i) => <i key={i} style={{ '--i': i }} />)}</div>}
                </div>
            )}

            {premio.id === 'calavera' && (
                <div className="ep-calavera-wrap">
                    {detalle?.jugador && <Carta jugador={detalle.jugador} clase={paso >= 1 ? 'ep-muere' : ''} />}
                    <svg viewBox="0 0 120 120" className="ep-craneo">
                        <Icono nombre="calavera" cx={60} cy={60} size={100} />
                    </svg>
                    <div className="ep-humo">{Array.from({ length: 7 }).map((_, i) => <i key={i} style={{ '--i': i }} />)}</div>
                </div>
            )}

            {(premio.id === 'switch_bueno' || premio.id === 'switch_malo') && (
                <div className={`ep-switch-wrap ${premio.id === 'switch_bueno' ? 'sube' : 'baja'}`}>
                    <Carta jugador={detalle?.viejo} clase={paso >= 1 ? 'ep-se-va' : ''} />
                    <svg viewBox="0 0 60 60" className="ep-flecha">
                        <Icono nombre={premio.id === 'switch_bueno' ? 'subeJugador' : 'bajaJugador'} cx={30} cy={30} size={54} />
                    </svg>
                    {paso >= 2 && <Carta jugador={detalle?.nuevo} clase="ep-llega" />}
                </div>
            )}

            {premio.id === 'apuesta' && detalle?.cartas && (
                <Apuesta cartas={detalle.cartas} onResolver={(j) => { onApuesta(j); onListo(); }} />
            )}

            {premio.id === 'autoabrir' && (
                <div className="ep-maletines">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <span key={i} className="ep-maletin" style={{ '--i': i }} />
                    ))}
                </div>
            )}

            {(premio.id === 'suben' || premio.id === 'bajan') && (
                <div className={`ep-monedas ${premio.id}`}>
                    {Array.from({ length: 12 }).map((_, i) => <i key={i} style={{ '--i': i }} />)}
                    <svg viewBox="0 0 90 90" className="ep-moneda-grande">
                        <Icono nombre={premio.id === 'suben' ? 'mercadoCaro' : 'mercadoBarato'} cx={45} cy={45} size={80} />
                    </svg>
                </div>
            )}

            {/* La apuesta tiene sus propios botones: se cierra sola al resolverse. */}
            {premio.id !== 'apuesta' && <button className="deal-si ep-seguir" onClick={onListo}>Seguir</button>}
        </div>
    );
}
